<script setup lang="ts">
import { computed } from 'vue';

import { useBookingQuoteStore } from '@/entities/quickQuote/lib/booking-store';
import { US_STATES } from '@/shared/constants';

import type { EstimateVehicle } from '@/entities/quickQuote/lib/booking-api.types';

const props = defineProps<{
	vehicle: EstimateVehicle;
}>();

const bookingStore = useBookingQuoteStore();

const vehicleLogo = computed(() => {
	try {
		return require(`@/assets/img/car-logos/${props.vehicle.makeId}.png`);
	} catch (err) {
		return null;
	}
});

const hasPlate = computed(() => Boolean(bookingStore.vehiclePlate?.number));

const plateNumber = computed(() => {
	if (bookingStore.vehiclePlate?.number) {
		return bookingStore.vehiclePlate.number.toUpperCase();
	}

	return '';
});
</script>

<template>
	<div
		v-if="vehicle"
		class="tw3-flex tw3-flex-col tw3-items-stretch tw3-gap-5 tw3-rounded-2xl tw3-border tw3-border-surface-200 tw3-bg-surface-0 tw3-p-5 tw3-shadow-sm sm:tw3-flex-row sm:tw3-items-center"
	>
		<!-- Graphic ~160x80 gif -->
		<div
			class="tw3-mx-auto tw3-flex tw3-h-20 tw3-w-40 tw3-shrink-0 tw3-items-center tw3-justify-center tw3-overflow-hidden tw3-rounded-xl sm:tw3-mx-0"
		>
			<img
				v-if="vehicle.imageUrl"
				alt="vehicle"
				class="tw3-h-full tw3-w-full tw3-object-contain"
				:src="vehicle.imageUrl"
			/>
			<i v-else class="pi pi-car !tw3-text-3xl tw3-text-surface-300" />
		</div>

		<!-- Info -->
		<div class="tw3-min-w-0 tw3-flex-1">
			<div class="tw3-flex tw3-items-start tw3-justify-between tw3-gap-4">
				<div>
					<div
						class="tw3-text-lg tw3-font-bold tw3-leading-tight tw3-text-surface-900"
					>
						{{ vehicle.year }}
						{{ vehicle.make }}
						{{ vehicle.model }}
					</div>
					<p class="tw3-mt-0.5 tw3-text-sm tw3-text-surface-500">
						{{ vehicle.subModel }} {{ vehicle.style }}
					</p>
				</div>
			</div>

			<div class="tw3-mt-3 tw3-flex tw3-flex-wrap tw3-items-end tw3-gap-3">
				<!-- VIN -->
				<div
					v-if="vehicle.vin"
					class="tw3-flex tw3-items-center tw3-gap-2 tw3-rounded-lg tw3-border tw3-border-surface-200 tw3-bg-surface-50 tw3-px-2.5 tw3-py-1.5"
				>
					<span
						class="tw3-text-[0.6rem] tw3-font-bold tw3-tracking-wider tw3-text-surface-400"
					>
						VIN
					</span>
					<span
						class="tw3-font-mono tw3-text-xs tw3-tracking-wider tw3-text-surface-800"
					>
						{{ vehicle.vin }}
					</span>
					<span
						class="tw3-flex tw3-size-4 tw3-items-center tw3-justify-center tw3-rounded-full tw3-bg-primary-500"
					>
						<i
							class="pi pi-check !tw3-text-[0.45rem] tw3-font-bold tw3-text-white"
						/>
					</span>
				</div>

				<!-- Plate -->
				<div
					v-if="hasPlate"
					class="tw3-inline-flex tw3-flex-col tw3-items-center tw3-rounded-md tw3-border-2 tw3-border-surface-300 tw3-bg-surface-50 tw3-px-2.5 tw3-py-0.5 tw3-leading-tight"
				>
					<div class="tw3-flex tw3-justify-center">
						<i
							class="pi pi-circle-fill tw3-mt-1"
							style="color: #708090; font-size: 0.2rem"
						></i>
						<div class="tw3-mx-2 tw3-text-[0.6rem] tw3-text-gray-500">
							{{
								US_STATES.find(
									v => v.value === bookingStore.vehiclePlate?.state
								)?.title || 'STATE'
							}}
						</div>
						<i
							class="pi pi-circle-fill tw3-mt-1"
							style="color: #708090; font-size: 0.2rem"
						></i>
					</div>
					<span
						class="tw3-text-sm tw3-font-bold tw3-tracking-wide tw3-text-surface-700"
					>
						{{ plateNumber }}
					</span>
				</div>
			</div>
		</div>
		<img
			v-if="vehicleLogo"
			alt="logo"
			class="tw3-h-20 tw3-shrink-0 tw3-object-contain"
			:src="vehicleLogo"
		/>
	</div>
</template>
